import { observer } from "mobx-react-lite"

import { ShowWhenTrue } from "@/components/conditionals"
import { Pagination } from "@/components/pagination"
import { Switch } from "@/components/switch"
import { Compound as Table } from "@/components/table"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { SwitchInactivePairs } from "@/domains/common/components/switches/switch-inactive-pairs"
import { MinimalPoolStore } from "@/domains/minimal-pool/minimal-pool-store"
import { ThApr } from "@/domains/pools/components/table-headers/th-apr"
import { ThDisplayName } from "@/domains/pools/components/table-headers/th-display-name"
import { ThMyPool } from "@/domains/pools/components/table-headers/th-my-pool"
import { ThMyStaked } from "@/domains/pools/components/table-headers/th-my-staked"
import { ThStaked } from "@/domains/pools/components/table-headers/th-staked"
import { ThTvlUsd } from "@/domains/pools/components/table-headers/th-tvl-usd"
import { ThWalletBalanceValueUsd } from "@/domains/pools/components/table-headers/wallet-balance-value-usd"
import { paginate, Sort } from "@/domains/table-processor"
import { useChainConfig } from "@/hooks/use-chain-config"
import { HandleZapClick } from "@/pages/liquidity"

import { LiquidityTableRow } from "./liquidity-table-row"
import { poolProcessor } from "./pool-processor"

export const LiquidityTable = observer(({ handleZapClick }: { handleZapClick: HandleZapClick }) => {
  const scaleApp = useScaleApplication()
  const chainConfig = useChainConfig()

  const currentPageNumber = scaleApp.Pages.Liquidity.currentPageNumber
  const isCompact = !scaleApp.Pages.Liquidity.isDetailed
  const poolType = scaleApp.Pages.Liquidity.poolType
  const pageSize = scaleApp.Pages.Liquidity.pageSize
  const search = scaleApp.Pages.Liquidity.search
  const showInactivePairs = scaleApp.Pages.Liquidity.showInactivePairs
  const sortBy = scaleApp.Pages.Liquidity.sortBy
  const stakedOnly = scaleApp.Pages.Liquidity.stakedOnly
  const setSortBy = (value: Sort) => scaleApp.Pages.Liquidity.setSortByFromUi(value)

  // This is the raw minimal pools.
  const minimalPools = scaleApp.MinimalPools.Repository.getAll()

  // time for pair processor conversion to minimal pool store
  const processed = poolProcessor(
    minimalPools,
    search,
    stakedOnly,
    showInactivePairs,
    poolType === "cl" ? "all" : poolType,
    sortBy,
  )

  const paginated = paginate({
    currentPageNumber,
    pageSize,
    numberOfRows: processed.rows.length,
  })

  const hasTableRows = processed.rows.length > 0

  return (
    <TooltipProvider>
      <Table.Root>
        <Table.Head>
          <Table.HeaderRow>
            <Table.Th className="max-w-[80px] px-0 sm:px-0" classNameInner="justify-center" isSortDisabled={true}>
              &nbsp;
            </Table.Th>
            <ThDisplayName setSortBy={setSortBy} sortBy={sortBy} name="Pair" />
            <ThApr setSortBy={setSortBy} sortBy={sortBy} />
            <ThTvlUsd setSortBy={setSortBy} sortBy={sortBy} />
            <ThStaked setSortBy={setSortBy} sortBy={sortBy} />
            <ThWalletBalanceValueUsd setSortBy={setSortBy} sortBy={sortBy} />
            <ThMyPool setSortBy={setSortBy} sortBy={sortBy} />
            <ThMyStaked setSortBy={setSortBy} sortBy={sortBy} />
          </Table.HeaderRow>
        </Table.Head>
        <Table.Body>
          <ShowWhenTrue when={!hasTableRows}>
            <Table.Tr>
              <Table.Td colSpan={9} className="py-6 text-center">
                No pools found, please adjust filters.
              </Table.Td>
            </Table.Tr>
          </ShowWhenTrue>
          <ShowWhenTrue when={hasTableRows}>
            {processed.rows
              .slice(paginated.indexes.firstItemIndexOnPage, paginated.indexes.lastItemIndexOnPage)
              .map((pool: MinimalPoolStore) => (
                <LiquidityTableRow
                  pool={pool}
                  chainConfig={chainConfig}
                  key={pool.address.toString()}
                  isCompact={isCompact}
                  handleZapClick={handleZapClick}
                />
              ))}
          </ShowWhenTrue>
        </Table.Body>
      </Table.Root>
      <div className="flex justify-between">
        <SwitchInactivePairs
          checked={showInactivePairs}
          onCheckedChange={(value) => scaleApp.Pages.Liquidity.setShowInactivePairsFromUi(value)}
        />
        <div className="flex-grow">
          <Pagination
            paginated={paginated}
            onChangeRowsPerPage={(rowsPerPage) => {
              scaleApp.Pages.Liquidity.setPageSizeFromUi(rowsPerPage)
              scaleApp.Pages.Liquidity.setCurrentPageNumberFromUi(1)
            }}
            onClickPreviousPage={(previousPageNumber) =>
              scaleApp.Pages.Liquidity.setCurrentPageNumberFromUi(previousPageNumber)
            }
            onClickNextPage={(nextPageNumber) => scaleApp.Pages.Liquidity.setCurrentPageNumberFromUi(nextPageNumber)}
          />
        </div>
      </div>
    </TooltipProvider>
  )
})

export default LiquidityTable
